import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ConfigProvider, theme as antdTheme, App as AntApp } from "antd";

type ThemeMode = "light" | "dark";
type ThemeCtx = { mode: ThemeMode; isDark: boolean; toggle: () => void; setMode: (m: ThemeMode) => void; };

const ThemeContext = createContext<ThemeCtx | null>(null);

function getInitialMode(): ThemeMode {
  const saved = localStorage.getItem("themeMode") as ThemeMode | null;
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light";
}

function ThemeProvider({ children }: React.PropsWithChildren) {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode());
  const isDark = mode === "dark";

  useEffect(() => {
    localStorage.setItem("themeMode", mode);
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  const value = useMemo<ThemeCtx>(() => ({
    mode, isDark,
    toggle: () => setMode(p => (p === "dark" ? "light" : "dark")),
    setMode,
  }), [mode, isDark]);

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider
        theme={{
          algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
          token: { colorPrimary: "#5f69bf" },
        }}
      >
        <AntApp>{children}</AntApp>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}

export function useThemeMode() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useThemeMode must be used within ThemeProvider");
  return ctx;
}

export default ThemeProvider;
